import React, { useEffect, useCallback, useState, useRef } from 'react';
import './Lightbox.css';

const MIN_MARQUEE_PX = 8; // displayed pixels — smaller drags are treated as clicks

const Lightbox = ({ isOpen, currentIndex, images, onClose, onNavigate, onRetouched }) => {
  // --- Marquee crop state (explicit Crop mode; saves as a NEW sibling file) ---
  const [cropMode, setCropMode] = useState(false);
  const [marquee, setMarquee] = useState(null); // {x, y, w, h}
  const [isDragging, setIsDragging] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [cropError, setCropError] = useState(null);
  const dragStart = useRef(null);
  const imgRef = useRef(null);
  const wrapperRef = useRef(null);

  // --- Retouch (generative fill) state ---
  const [fluxAvailable, setFluxAvailable] = useState(false);
  const [retouchMode, setRetouchMode] = useState(false);
  const [brushSize, setBrushSize] = useState(75);
  const [softness, setSoftness] = useState(30);
  const [brushOpacity, setBrushOpacity] = useState(100);
  const [retouchPrompt, setRetouchPrompt] = useState('');
  const [hasStrokes, setHasStrokes] = useState(false);
  const [isRetouching, setIsRetouching] = useState(false);
  const [retouchError, setRetouchError] = useState(null);
  const [retouchProgress, setRetouchProgress] = useState(null); // {phase, step, total}
  const [cursorPos, setCursorPos] = useState(null); // brush ring position
  const paintCanvasRef = useRef(null);
  const isPaintingRef = useRef(false);
  const lastPointRef = useRef(null);
  const undoStackRef = useRef([]);

  const clearMarquee = useCallback(() => {
    setMarquee(null);
    setIsDragging(false);
    setShowConfirm(false);
    setCropError(null);
    dragStart.current = null;
  }, []);

  const exitCrop = useCallback(() => {
    setCropMode(false);
    clearMarquee();
  }, [clearMarquee]);

  const exitRetouch = useCallback(() => {
    setRetouchMode(false);
    setHasStrokes(false);
    setRetouchError(null);
    setCursorPos(null);
    undoStackRef.current = [];
    isPaintingRef.current = false;
    lastPointRef.current = null;
  }, []);

  // Poll live fill progress while a retouch job runs (phase + step 1..28)
  useEffect(() => {
    if (!isRetouching) {
      setRetouchProgress(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const status = await window.electronAPI.getFillStatus();
        if (!cancelled && status && status.active) {
          setRetouchProgress({ phase: status.phase, step: status.step, total: status.total });
        }
      } catch (e) { /* sidecar may still be starting — keep polling */ }
    };
    poll();
    const interval = setInterval(poll, 500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isRetouching]);

  // Retouch is available everywhere: local FLUX dev when present, otherwise
  // FLUX Fill Pro in the cloud (retouchEngine tells us which).
  useEffect(() => {
    if (isOpen && window.electronAPI && window.electronAPI.getAIConfig) {
      window.electronAPI.getAIConfig()
        .then((cfg) => setFluxAvailable(!!cfg.retouchEngine))
        .catch(() => setFluxAvailable(false));
    }
  }, [isOpen]);

  // Reset tools when the image changes or lightbox closes
  useEffect(() => {
    setCropMode(false);
    clearMarquee();
    exitRetouch();
  }, [currentIndex, isOpen, clearMarquee, exitRetouch]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Don't react while typing in the prompt field
      if (e.target && e.target.tagName === 'INPUT') {
        if (e.key === 'Escape') e.target.blur();
        return;
      }
      switch(e.key) {
        case 'Escape':
          // ESC backs out one layer at a time: tool → selection → close
          if (retouchMode) {
            if (!isRetouching) exitRetouch();
          } else if (cropMode) {
            if (marquee || showConfirm) {
              clearMarquee(); // back to an empty crop selection
            } else {
              exitCrop();
            }
          } else {
            onClose();
          }
          break;
        case 'ArrowLeft':
          if (!retouchMode && !cropMode) onNavigate('prev');
          break;
        case 'ArrowRight':
          if (!retouchMode && !cropMode) onNavigate('next');
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNavigate, marquee, showConfirm, clearMarquee, retouchMode, isRetouching, exitRetouch, cropMode, exitCrop]);

  // Prevent background scrolling when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleBackgroundClick = useCallback((e) => {
    if (e.target.classList.contains('lightbox-overlay') && !retouchMode && !cropMode) {
      onClose();
    }
  }, [onClose, retouchMode, cropMode]);

  // --- Marquee drag handlers (active only in Crop mode) ---

  // The <img> uses object-fit: contain, so its layout box can be WIDER or
  // TALLER than the picture actually drawn inside it (letterboxing — e.g. a
  // portrait image in a wide window gets a ~1500px box but only ~700px of
  // pixels). Every screen→image mapping (marquee, paint canvas, mask) must
  // use this drawn rect, never img.clientWidth/Height, or crops come out
  // squeezed/offset (the 3:4 → 814×1866 bug).
  const getRenderedImageRect = () => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;
    const boxW = img.clientWidth;
    const boxH = img.clientHeight;
    const scale = Math.min(boxW / img.naturalWidth, boxH / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    return {
      x: img.offsetLeft + (boxW - w) / 2, // relative to the wrapper (offsetParent)
      y: img.offsetTop + (boxH - h) / 2,
      w,
      h,
      scaleX: img.naturalWidth / w,
      scaleY: img.naturalHeight / h
    };
  };

  // Pointer position relative to the wrapper, clamped to the DRAWN picture
  const getRelativePos = (e) => {
    const rect = wrapperRef.current.getBoundingClientRect();
    const r = getRenderedImageRect() || { x: 0, y: 0, w: rect.width, h: rect.height };
    return {
      x: Math.min(Math.max(e.clientX - rect.left, r.x), r.x + r.w),
      y: Math.min(Math.max(e.clientY - rect.top, r.y), r.y + r.h)
    };
  };

  const handleMouseDown = useCallback((e) => {
    if (!cropMode || retouchMode || e.button !== 0 || isCropping || !wrapperRef.current) return;
    e.preventDefault();
    const pos = getRelativePos(e);
    dragStart.current = pos;
    setMarquee({ x: pos.x, y: pos.y, w: 0, h: 0 });
    setIsDragging(true);
    setShowConfirm(false);
    setCropError(null);
  }, [isCropping, retouchMode, cropMode]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      if (!dragStart.current || !wrapperRef.current) return;
      const pos = getRelativePos(e);
      setMarquee({
        x: Math.min(dragStart.current.x, pos.x),
        y: Math.min(dragStart.current.y, pos.y),
        w: Math.abs(pos.x - dragStart.current.x),
        h: Math.abs(pos.y - dragStart.current.y)
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setMarquee((m) => {
        if (!m || m.w < MIN_MARQUEE_PX || m.h < MIN_MARQUEE_PX) {
          dragStart.current = null;
          return null; // treat as a click — no selection
        }
        setShowConfirm(true);
        return m;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Translate the displayed-pixel marquee into natural image pixels
  const marqueeToNaturalRect = () => {
    const r = getRenderedImageRect();
    return {
      left: (marquee.x - r.x) * r.scaleX,
      top: (marquee.y - r.y) * r.scaleY,
      width: marquee.w * r.scaleX,
      height: marquee.h * r.scaleY
    };
  };

  const handleCrop = async () => {
    if (!marquee || !imgRef.current || isCropping) return;
    const currentImage = images[currentIndex];
    setIsCropping(true);
    setCropError(null);
    try {
      const result = await window.electronAPI.cropImage(currentImage.path, marqueeToNaturalRect());
      if (result.success) {
        // Saved as a new -cropNN sibling — add it to the grid like a retouch
        exitCrop();
        if (onRetouched) {
          onRetouched(result);
        }
      } else {
        setCropError(result.error || 'Crop failed');
      }
    } catch (err) {
      console.error('Crop failed:', err);
      setCropError(err.message || 'Crop failed');
    } finally {
      setIsCropping(false);
    }
  };

  // --- Retouch painting ---

  const enterRetouch = () => {
    clearMarquee();
    setRetouchMode(true);
    setRetouchError(null);
    setHasStrokes(false);
    undoStackRef.current = [];
    // Size the paint canvas to the displayed image on next tick
    requestAnimationFrame(() => {
      const canvas = paintCanvasRef.current;
      const r = getRenderedImageRect();
      if (canvas && r) {
        // Match the DRAWN picture, not the (possibly letterboxed) img box,
        // so strokes land where they were painted once scaled to the mask
        canvas.width = Math.round(r.w);
        canvas.height = Math.round(r.h);
        canvas.style.left = `${r.x}px`;
        canvas.style.top = `${r.y}px`;
        canvas.style.width = `${r.w}px`;
        canvas.style.height = `${r.h}px`;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    });
  };

  const stampBrush = (ctx, x, y) => {
    const r = Math.max(2, brushSize / 2);
    const soft = Math.min(0.98, softness / 100);
    const a = brushOpacity / 100;
    const inner = r * (1 - soft);
    const g = ctx.createRadialGradient(x, y, Math.max(0, inner), x, y, r);
    // Painted PINK for visibility on light images; recolored to white at
    // mask-export time (the model needs white = regenerate)
    g.addColorStop(0, `rgba(255,34,119,${a})`);
    g.addColorStop(1, 'rgba(255,34,119,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  };

  const paintTo = (x, y) => {
    const canvas = paintCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const last = lastPointRef.current;
    if (last) {
      // Stamp along the segment so fast strokes stay solid
      const dist = Math.hypot(x - last.x, y - last.y);
      const spacing = Math.max(1, brushSize / 6);
      const steps = Math.max(1, Math.floor(dist / spacing));
      for (let i = 1; i <= steps; i++) {
        stampBrush(ctx, last.x + ((x - last.x) * i) / steps, last.y + ((y - last.y) * i) / steps);
      }
    } else {
      stampBrush(ctx, x, y);
    }
    lastPointRef.current = { x, y };
  };

  const handlePaintDown = (e) => {
    if (e.button !== 0 || isRetouching) return;
    e.preventDefault();
    const canvas = paintCanvasRef.current;
    const ctx = canvas.getContext('2d');
    // Snapshot for undo (cap the stack to bound memory)
    undoStackRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    if (undoStackRef.current.length > 20) undoStackRef.current.shift();

    isPaintingRef.current = true;
    lastPointRef.current = null;
    const rect = canvas.getBoundingClientRect();
    paintTo(e.clientX - rect.left, e.clientY - rect.top);
    setHasStrokes(true);
  };

  const handlePaintMove = (e) => {
    const canvas = paintCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // Cursor ring lives in the wrapper; offset by the canvas position inside it
    setCursorPos({
      x: canvas.offsetLeft + (e.clientX - rect.left),
      y: canvas.offsetTop + (e.clientY - rect.top)
    });
    if (isPaintingRef.current) {
      paintTo(e.clientX - rect.left, e.clientY - rect.top);
    }
  };

  const handlePaintUp = () => {
    isPaintingRef.current = false;
    lastPointRef.current = null;
  };

  const handleUndo = () => {
    const canvas = paintCanvasRef.current;
    const snapshot = undoStackRef.current.pop();
    if (canvas && snapshot) {
      canvas.getContext('2d').putImageData(snapshot, 0, 0);
      if (undoStackRef.current.length === 0) {
        // Heuristic: stack empty means we're back at a blank canvas
        setHasStrokes(false);
      }
    }
  };

  const handleClearStrokes = () => {
    const canvas = paintCanvasRef.current;
    if (canvas) {
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    }
    undoStackRef.current = [];
    setHasStrokes(false);
  };

  const handleTouchUp = async () => {
    const canvas = paintCanvasRef.current;
    const img = imgRef.current;
    const currentImage = images[currentIndex];
    if (!canvas || !img || !hasStrokes || isRetouching) return;

    setIsRetouching(true);
    setRetouchError(null);
    try {
      // Recolor the pink display strokes to WHITE, preserving per-pixel alpha
      // (the model's mask semantics are white = regenerate)
      const recolored = document.createElement('canvas');
      recolored.width = canvas.width;
      recolored.height = canvas.height;
      const rctx = recolored.getContext('2d');
      rctx.drawImage(canvas, 0, 0);
      rctx.globalCompositeOperation = 'source-in';
      rctx.fillStyle = '#ffffff';
      rctx.fillRect(0, 0, recolored.width, recolored.height);

      // Export the mask at NATURAL resolution: black background, white strokes
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = img.naturalWidth;
      maskCanvas.height = img.naturalHeight;
      const mctx = maskCanvas.getContext('2d');
      mctx.fillStyle = '#000000';
      mctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
      mctx.drawImage(recolored, 0, 0, maskCanvas.width, maskCanvas.height);
      const maskBase64 = maskCanvas.toDataURL('image/png');

      const result = await window.electronAPI.retouchImage(
        currentImage.path, maskBase64, retouchPrompt.trim(), null
      );

      if (result.success) {
        exitRetouch();
        if (onRetouched) {
          onRetouched(result);
        }
      } else {
        setRetouchError(result.error || 'Retouch failed');
      }
    } catch (err) {
      console.error('Retouch failed:', err);
      setRetouchError(err.message || 'Retouch failed');
    } finally {
      setIsRetouching(false);
    }
  };

  if (!isOpen || !images || images.length === 0) return null;

  const currentImage = images[currentIndex];
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < images.length - 1;

  // SVG: vector source — viewable, but Retouch/Crop are raster tools (Sharp
  // cannot write SVG output), so they are hidden for these files. SVGs
  // without width/height attributes also need an explicit display size.
  const isSvg = /\.svg$/i.test(currentImage.path);

  // file:// is blocked by Chromium in dev (localhost origin) — use the same
  // local-image:// protocol the thumbnails use. The ?v= suffix cache-busts
  // after an in-place crop (the main-process protocol handler strips it).
  const imgSrc = window.electronAPI
    ? `local-image://${encodeURIComponent(currentImage.path)}${currentImage.version ? `?v=${currentImage.version}` : ''}`
    : `file://${currentImage.path}`;

  // Natural-pixel size of the current selection for the info label
  let selectionLabel = null;
  const renderedRect = marquee ? getRenderedImageRect() : null;
  if (marquee && renderedRect) {
    selectionLabel = `${Math.round(marquee.w * renderedRect.scaleX)} × ${Math.round(marquee.h * renderedRect.scaleY)} px`;
  }

  return (
    <div className="lightbox-overlay" onClick={handleBackgroundClick}>
      <div className="lightbox-container">
        {/* Close button */}
        <button className="lightbox-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {/* Tool buttons: Retouch (local FLUX machines only) + Crop.
            Hidden for SVG — raster tools can't write vector output. */}
        {!retouchMode && !isSvg && (
          <div className="lightbox-tools">
            {fluxAvailable && !cropMode && (
              <button className="lightbox-retouch-btn" onClick={enterRetouch} title="Retouch — paint an area and let AI regenerate it">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08"></path>
                  <path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z"></path>
                </svg>
                Retouch
              </button>
            )}
            <button
              className={`lightbox-crop-btn ${cropMode ? 'active' : ''}`}
              onClick={() => (cropMode ? exitCrop() : (exitRetouch(), setCropMode(true)))}
              title={cropMode ? 'Exit crop mode' : 'Crop — drag a rectangle, the crop is saved as a new image'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"></path>
                <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"></path>
              </svg>
              {cropMode ? 'Exit Crop' : 'Crop'}
            </button>
            {cropMode && !marquee && (
              <span className="crop-hint">Drag a rectangle over the image</span>
            )}
          </div>
        )}

        {/* Previous button */}
        <button
          className={`lightbox-nav lightbox-nav-prev ${(!canGoPrev || retouchMode || cropMode) ? 'disabled' : ''}`}
          onClick={() => canGoPrev && !retouchMode && !cropMode && onNavigate('prev')}
          disabled={!canGoPrev || retouchMode || cropMode}
          aria-label="Previous image"
        >
          ‹
        </button>

        {/* Image container */}
        <div className="lightbox-image-container">
          <div
            className={`lightbox-image-wrapper ${retouchMode ? 'retouch-active' : ''} ${cropMode ? 'crop-active' : ''}`}
            ref={wrapperRef}
            onMouseDown={handleMouseDown}
          >
            <img
              ref={imgRef}
              src={imgSrc}
              alt={currentImage.name}
              className={`lightbox-image ${isSvg ? 'lightbox-image-svg' : ''}`}
              draggable={false}
              onError={(e) => {
                console.error('Failed to load image:', currentImage.path);
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzMzMyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOTk5IiBkeT0iLjNlbSI+RXJyb3I8L3RleHQ+PC9zdmc+';
              }}
            />

            {/* Retouch paint layer */}
            {retouchMode && (
              <>
                <canvas
                  ref={paintCanvasRef}
                  className="retouch-canvas"
                  onMouseDown={handlePaintDown}
                  onMouseMove={handlePaintMove}
                  onMouseUp={handlePaintUp}
                  onMouseLeave={() => { handlePaintUp(); setCursorPos(null); }}
                />
                {cursorPos && !isRetouching && (
                  <div
                    className="brush-cursor"
                    style={{
                      left: cursorPos.x,
                      top: cursorPos.y,
                      width: brushSize,
                      height: brushSize
                    }}
                  />
                )}
                {isRetouching && (
                  <div className="retouch-busy">
                    <div className="retouch-spinner"></div>
                    <div className="retouch-busy-text">
                      {retouchProgress && retouchProgress.total > 0 ? (
                        <>
                          {retouchProgress.phase}… step {retouchProgress.step} of {retouchProgress.total}
                        </>
                      ) : (
                        <>
                          {retouchProgress && retouchProgress.phase
                            ? retouchProgress.phase
                            : 'Retouching with FLUX…'}
                          <br />
                          <span>~30s (first use loads the model, 1–2 min)</span>
                        </>
                      )}
                    </div>
                    {retouchProgress && retouchProgress.total > 0 && (
                      <div className="retouch-progress-track">
                        <div
                          className="retouch-progress-fill"
                          style={{ width: `${Math.round((retouchProgress.step / retouchProgress.total) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Marquee selection rectangle */}
            {!retouchMode && marquee && (
              <div
                className="lightbox-marquee"
                style={{
                  left: marquee.x,
                  top: marquee.y,
                  width: marquee.w,
                  height: marquee.h
                }}
              >
                {selectionLabel && (
                  <div className="marquee-dimensions">{selectionLabel}</div>
                )}
              </div>
            )}

            {/* Crop / Cancel confirmation bar */}
            {!retouchMode && showConfirm && marquee && (
              <div
                className="marquee-confirm"
                style={{
                  left: marquee.x + marquee.w / 2,
                  top: marquee.y + marquee.h + 12
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {cropError && <span className="marquee-error">{cropError}</span>}
                <button
                  className="marquee-btn marquee-btn-crop"
                  onClick={handleCrop}
                  disabled={isCropping}
                >
                  {isCropping ? 'Cropping…' : 'OK'}
                </button>
                <button
                  className="marquee-btn marquee-btn-cancel"
                  onClick={clearMarquee}
                  disabled={isCropping}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Image info (hidden while retouching to make room for the toolbar) */}
          {!retouchMode && (
            <div className="lightbox-info">
              <div className="lightbox-filename">{currentImage.name}</div>
              {currentImage.metadata && (
                <div className="lightbox-dimensions">
                  {currentImage.metadata.width} × {currentImage.metadata.height}
                </div>
              )}
              <div className="lightbox-counter">
                {currentIndex + 1} / {images.length}
              </div>
            </div>
          )}

          {/* Retouch toolbar */}
          {retouchMode && (
            <div className="retouch-toolbar" onMouseDown={(e) => e.stopPropagation()}>
              <div className="retouch-slider-group">
                <span className="retouch-label">Brush</span>
                <input type="range" min="10" max="300" value={brushSize}
                  onChange={(e) => setBrushSize(parseInt(e.target.value))} disabled={isRetouching} />
                <span className="retouch-value">{brushSize}px</span>
              </div>
              <div className="retouch-slider-group">
                <span className="retouch-label">Soft</span>
                <input type="range" min="0" max="95" value={softness}
                  onChange={(e) => setSoftness(parseInt(e.target.value))} disabled={isRetouching} />
                <span className="retouch-value">{softness}%</span>
              </div>
              <div className="retouch-slider-group">
                <span className="retouch-label">Opacity</span>
                <input type="range" min="10" max="100" value={brushOpacity}
                  onChange={(e) => setBrushOpacity(parseInt(e.target.value))} disabled={isRetouching} />
                <span className="retouch-value">{brushOpacity}%</span>
              </div>
              <input
                type="text"
                className="retouch-prompt"
                placeholder="What should appear? Leave empty to remove."
                title="Describe what should APPEAR in the painted area (e.g. 'bare skin', 'brick wall'). Leave empty to remove the object — the AI continues the surroundings."
                value={retouchPrompt}
                onChange={(e) => setRetouchPrompt(e.target.value)}
                disabled={isRetouching}
              />
              <button className="marquee-btn marquee-btn-cancel" onClick={handleUndo}
                disabled={isRetouching || undoStackRef.current.length === 0}>
                Undo
              </button>
              <button className="marquee-btn marquee-btn-cancel" onClick={handleClearStrokes}
                disabled={isRetouching || !hasStrokes}>
                Clear
              </button>
              <button className="marquee-btn marquee-btn-cancel" onClick={exitRetouch}
                disabled={isRetouching}>
                Cancel
              </button>
              <button className="marquee-btn marquee-btn-crop retouch-go" onClick={handleTouchUp}
                disabled={isRetouching || !hasStrokes}>
                {isRetouching ? 'Retouching…' : 'Touch Up Image'}
              </button>
              {retouchError && <span className="marquee-error">{retouchError}</span>}
            </div>
          )}
        </div>

        {/* Next button */}
        <button
          className={`lightbox-nav lightbox-nav-next ${(!canGoNext || retouchMode || cropMode) ? 'disabled' : ''}`}
          onClick={() => canGoNext && !retouchMode && !cropMode && onNavigate('next')}
          disabled={!canGoNext || retouchMode || cropMode}
          aria-label="Next image"
        >
          ›
        </button>
      </div>
    </div>
  );
};

export default Lightbox;
